<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <div
          class="inspection-header d-flex align-center justify-space-between"
          style="width: 100%; flex-wrap: nowrap"
        >
          <!-- LEFT: Back + Title -->
          <div class="d-flex align-center" style="gap: 12px">
            <v-btn icon small @click="$router.back()">
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <div class="d-flex flex-column">
              <div class="inspection-page-title">{{ inspectionName }}</div>
              <div class="inspection-page-subtitle">Inspection and Findings</div>
            </div>
          </div>

          <!-- RIGHT: Buttons -->
          <div class="d-flex align-center" style="gap: 8px; white-space: nowrap">
            <audit-history-button
              v-if="inspection && inspection.inspectionId"
              entity-type="Inspection"
              :record-id="inspection.inspectionId"
              title="Inspection Change History"
            />
            <v-btn
              color="primary"
              dark
              small
              flat
              @click="onPrintInspection"
              :disabled="!inspection || !inspection.inspectionId || loading"
            >
              <v-icon left size="18">mdi-file-pdf-box</v-icon>
              Export PDF
            </v-btn>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Inspection Details card -->
    <v-row>
      <v-col cols="12">
        <v-card flat class="pa-5 inspection-details-card" elevation="1">
          <div class="inspection-details-card__header">
            <div class="inspection-details-card__title">Inspection Details</div>
            <div class="inspection-details-card__type">
              <span class="inspection-details-card__type-label">Status</span>
              <v-chip
                v-if="inspectionTypeLabel"
                :color="inspectionTypeColour"
                variant="flat"
                size="small"
                class="font-weight-bold"
              >
                {{ inspectionTypeLabel }}
              </v-chip>
            </div>
          </div>

          <div class="inspection-details-card__grid">
            <div class="inspection-detail-row">
              <div class="inspection-detail-row__label">Inspection Type (Why)</div>
              <div class="inspection-detail-row__value">{{ inspection?.inspectionTypeName || "Not recorded" }}</div>
            </div>
            <div class="inspection-detail-row">
              <div class="inspection-detail-row__label">Zone</div>
              <div class="inspection-detail-row__value">
                {{ inspection?.zoneNameFK || inspection?.zoneName || inspection?.zoneId || "—" }}
              </div>
            </div>

            <div class="inspection-detail-row">
              <div class="inspection-detail-row__label">Inspection Title</div>
              <div class="inspection-detail-row__value">
                {{ inspection?.name || "—" }}
              </div>
            </div>

            <div class="inspection-detail-row">
              <div class="inspection-detail-row__label">Inspection Description</div>
              <div class="inspection-detail-row__value">
                {{ inspection?.description || "—" }}
              </div>
            </div>

            <!-- Show Due date when not started; Started/Finished once underway -->
            <div v-if="!inspection?.startTime && inspection?.dueDate" class="inspection-detail-row">
              <div class="inspection-detail-row__label">Due date</div>
              <div class="inspection-detail-row__value">
                {{ formatDateOnly(inspection.dueDate) }}
              </div>
            </div>

            <div v-if="inspection?.startTime" class="inspection-detail-row">
              <div class="inspection-detail-row__label">Started</div>
              <div class="inspection-detail-row__value">
                <div>{{ formatDateTime(inspection.startTime) }}</div>
                <div
                  v-if="inspection.startLatitude && inspection.startLongitude"
                  class="inspection-detail-row__sub"
                >
                  {{ inspection.startLatitude.toFixed(6) }}, {{ inspection.startLongitude.toFixed(6) }}
                </div>
              </div>
            </div>

            <div v-if="inspection?.endTime" class="inspection-detail-row">
              <div class="inspection-detail-row__label">Finished</div>
              <div class="inspection-detail-row__value">
                <div>{{ formatDateTime(inspection.endTime) }}</div>
                <div
                  v-if="inspection.endLatitude && inspection.endLongitude"
                  class="inspection-detail-row__sub"
                >
                  {{ inspection.endLatitude.toFixed(6) }}, {{ inspection.endLongitude.toFixed(6) }}
                </div>
              </div>
            </div>
          </div>

          <div v-if="findings.length > 0" class="inspection-details-card__counts">
            <div class="count-pill count-pill--total">
              <span class="count-pill__num">{{ findings.length }}</span>
              <span class="count-pill__label">Total findings</span>
            </div>
            <div v-if="actionNeededCount > 0" class="count-pill count-pill--danger">
              <span class="count-pill__num">{{ actionNeededCount }}</span>
              <span class="count-pill__label">Action needed</span>
            </div>
            <div v-if="resolvedCount > 0" class="count-pill count-pill--success">
              <span class="count-pill__num">{{ resolvedCount }}</span>
              <span class="count-pill__label">Resolved</span>
            </div>
          </div>

          <div class="inspection-details-card__actions">
            <v-btn
              v-if="!inspection?.startTime"
              color="success"
              variant="flat"
              @click="onStartInspection"
              :loading="starting"
            >
              <v-icon start>mdi-play</v-icon>
              Start Inspection
            </v-btn>
            <v-btn
              v-if="inspection?.startTime && !inspection?.endTime"
              color="error"
              variant="flat"
              @click="onStopInspection"
              :loading="stopping"
            >
              <v-icon start>mdi-stop</v-icon>
              Stop Inspection
            </v-btn>
            <v-btn
              color="primary"
              variant="outlined"
              :disabled="!inspection || !inspection.inspectionId"
              @click="showFullMap = true"
            >
              <v-icon start>mdi-map-search</v-icon>
              View on Map
            </v-btn>
            <v-btn
              color="primary"
              variant="flat"
              @click="onAddFinding"
            >
              <v-icon start>mdi-plus</v-icon>
              Add Finding
            </v-btn>
            <v-btn
              color="teal-darken-2"
              variant="tonal"
              :disabled="!inspection || !inspection.inspectionId"
              :loading="pushingToEsri"
              @click="onPushToEsri"
              title="Push this Inspection to the ESRI Feature Layer configured under Customer Settings"
            >
              <v-icon start>mdi-cloud-upload</v-icon>
              Push to ESRI
            </v-btn>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <v-row style="margin-top: 0px">
      <v-col cols="12">
        <v-card elevation="1" class="inspection-findings-card">
          <!-- Card header with title, search, and add button -->
          <v-toolbar flat color="white" dense class="px-4 align-center inspection-findings-toolbar">
            <v-toolbar-title class="pa-0 ma-0 font-weight-medium">Findings</v-toolbar-title>

            <v-spacer />

            <v-text-field
              v-model="search"
              placeholder="Search"
              clearable
              append-inner-icon="mdi-magnify"
              hide-details
              density="compact"
              solo
              class="ma-0"
              style="max-width: 250px"
            />
          </v-toolbar>

          <!-- Findings Data table -->
          <v-data-table
            :headers="headers"
            :items="filteredFindings"
            :loading="loading"
            class="elevation-0 inspection-findings-table"
            item-key="findingId"
            density="comfortable"
            loading-text="Loading findings…"
            no-data-text="No findings found."
            :items-per-page="50"
            :items-per-page-options="[25, 50, 100, 200, -1]"
            striped
            @click:row="onRowClick"
          >
            <template #item.title="{ item }">
              <span
                class="text-decoration-none"
                style="cursor: pointer; color: blue"
                @click="viewFinding(item.findingId)"
              >
                {{ item.details || item.title }}
              </span>
            </template>

            <template #item.submitTime="{ item }">
              {{ formatDate(item.submitTime) }}
            </template>

            <template #item.submittedByFK="{ item }">
              <div class="submitted-by-cell">
                <v-avatar size="32" class="bg-avatar submitted-by-avatar">
                  <v-img :src="getUserIconUrl(item.ownerIconId)" />
                </v-avatar>
                <div class="submitted-by-text">
                  {{ item.submittedByFK || item.submittedBy || item.ownerName || "Unknown user" }}
                </div>
              </div>
            </template>

            <template #item.statusFK="{ item }">
              <v-chip
                v-if="getStatus(item.statusId) && getStatus(item.statusId).colour"
                :color="getStatus(item.statusId).colour.toLowerCase()"
                small
                text-color="white"
                variant="elevated"
              >
                {{ getStatus(item.statusId).findingStatus }}
              </v-chip>
              <v-chip v-else color="grey" small text-color="white" variant="elevated">
                {{ getStatus(item.statusId)?.findingStatus || 'No Status Set' }}
              </v-chip>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-if="inspection && inspection.inspectionId" class="mt-2">
      <v-col cols="12">
        <v-card elevation="1" class="inspection-map-card">
          <div class="inspection-map-card__header">
            <div class="inspection-map-card__title">
              <v-icon size="18" color="primary">mdi-map</v-icon>
              <span>Map of findings</span>
              <span class="inspection-map-card__count">
                {{ findings.length }}
                {{ findings.length === 1 ? "pin" : "pins" }}
              </span>
            </div>
          </div>
          <div class="inspection-map-card__preview" @click="showFullMap = true">
            <InspectionFieldMap
              :inspection="inspection"
              :findings="findings"
              :form-visuals-by-id="formVisualsById"
              compact
              @marker-click="onFieldMapMarkerClick"
            />
          </div>
          <button
            type="button"
            class="inspection-map-card__view-full"
            @click="showFullMap = true"
          >
            <v-icon size="20" color="primary">mdi-map-outline</v-icon>
            <span>VIEW FULL MAP</span>
          </button>
        </v-card>
      </v-col>
    </v-row>

    <!-- Full-Screen Map Dialog -->
    <v-dialog v-model="showFullMap" fullscreen scrollable transition="dialog-bottom-transition">
      <v-card class="fullmap-card">
        <v-toolbar color="primary" dark height="56">
          <v-btn variant="flat" color="white" class="text-primary mr-2" @click="showFullMap = false">
            <v-icon start>mdi-arrow-left</v-icon>
            Back to Inspection
          </v-btn>
          <v-toolbar-title class="text-white">
            {{ inspectionName }} — Map
          </v-toolbar-title>
          <v-spacer />
          <span class="text-white mr-3 text-caption font-weight-medium">
            {{ findings.length }} {{ findings.length === 1 ? "pin" : "pins" }}
          </span>
        </v-toolbar>
        <v-card-text class="pa-0 fullmap-card__body">
          <InspectionFieldMap
            v-if="showFullMap && inspection && inspection.inspectionId"
            :inspection="inspection"
            :findings="findings"
            :form-visuals-by-id="formVisualsById"
            @marker-click="onFieldMapMarkerClick"
          />
        </v-card-text>
        <!-- Floating fallback close button so it's impossible to miss on small screens -->
        <v-btn
          class="fullmap-fab-close"
          icon
          color="primary"
          size="large"
          elevation="6"
          @click="showFullMap = false"
        >
          <v-icon size="28">mdi-close</v-icon>
        </v-btn>
      </v-card>
    </v-dialog>

    <!-- Create Finding Modal -->
    <v-dialog
      v-model="showFindingModal"
      persistent
      max-width="900px"
    >
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span class="text-h6 font-weight-bold">Create New Finding</span>
          <v-btn flat icon @click="showFindingModal = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <v-form ref="findingForm" lazy-validation>
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field v-model="newFinding.title" label="Description" required />
                <v-textarea v-model="newFinding.details" label="Details" auto-grow required />
                <v-select
                  v-model="newFinding.statusId"
                  :items="findingStatuses"
                  item-title="findingStatus"
                  :item-value="(s) => Number(s.findingStatusId)"
                  label="Status"
                  required
                />
                <v-select
                  v-model="newFinding.priorityId"
                  :items="priorities"
                  item-title="priority"
                  :item-value="(p) => Number(p.priorityId)"
                  label="Priority"
                  required
                />

                <!-- Captured Location -->
                <div class="captured-location my-4">
                  <div class="captured-location__header">
                    <v-icon size="18" color="primary">mdi-crosshairs-gps</v-icon>
                    <span class="captured-location__label">Captured Location</span>
                  </div>
                  <div
                    v-if="newFinding.latitude && newFinding.longitude"
                    class="captured-location__body"
                  >
                    <div class="captured-location__coords">
                      {{ Number(newFinding.latitude).toFixed(6) }},
                      {{ Number(newFinding.longitude).toFixed(6) }}
                    </div>
                    <div
                      v-if="newFinding.accuracy != null"
                      class="captured-location__accuracy"
                    >
                      ± {{ Math.round(newFinding.accuracy) }} m
                    </div>
                  </div>
                  <div v-else class="captured-location__empty">
                    No GPS fix yet — switch to the Map tab and use “Add Form”.
                  </div>
                </div>
              </v-col>

              <v-col cols="12" md="6">
                <v-card variant="tonal" class="pa-3" title="Form">
                  <template #text>
                    <FormViewer
                      ref="formViewer"
                      v-if="selectedFormJson"
                      :formJson="selectedFormJson"
                      :title="newFinding.title || 'Finding form'"
                      submitLabel="Attach Form Response"
                      @submit="onFormSubmit"
                    />
                    <div v-else class="text-caption">Choose a form to render.</div>

                    <v-chip
                      v-if="formSubmitPayload"
                      class="mt-3"
                      color="success"
                      size="small"
                      label
                    >
                      Form response captured
                    </v-chip>
                  </template>
                </v-card>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="showFindingModal = false">Cancel</v-btn>
          <v-btn color="green" @click="saveFinding">Create Finding</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>

  <!-- Form Picker -->
  <v-dialog v-model="showFormPicker" max-width="640px">
    <v-card>
      <v-card-title class="text-h6">Choose a form</v-card-title>
      <v-card-text>
        <v-list density="compact">
          <v-list-item
            v-for="f in formTemplates"
            :key="f.formId"
            class="hoverable"
            @click="chooseForm(f)"
          >
            <v-list-item-title>{{ f.name }}</v-list-item-title>
            <v-list-item-subtitle>{{ f.category || "Form template" }}</v-list-item-subtitle>
            <template #append>
              <v-icon>mdi-chevron-right</v-icon>
            </template>
          </v-list-item>
        </v-list>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="showFormPicker = false">Cancel</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import FormViewer from "@/components/FormViewer.vue";
import InspectionFieldMap from "@/components/InspectionFieldMap.vue";
import { fetchFormsAll, fetchForm } from "@/api/formsTemplates";
import { getAllByInspectionId, createFinding, submitFormResponse } from "@/api/findings";
import { getById, deleteItem, startInspection, stopInspection } from "@/api/inspections";
import { getLookupDataByType } from "@/api/lookups";
import { generateInspectionReport } from "@/api/reports";
import { pushInspectionFindingsToEsri } from "@/api/esri";
import { icons, iconsNums } from "@/assets/icons";
import { useDialogStore } from "@/stores/dialog";
import { useToast } from "vue-toastification";

export default {
  name: "Inspection-",
  components: { FormViewer, InspectionFieldMap },
  setup() {
    const dialog = useDialogStore();
    const toast = useToast();
    return { dialog, toast };
  },
  data() {
    return {
      headers: [
        { title: "Description", key: "title" },
        { title: "Zone", key: "zoneNameFK" },
        { title: "Submitted By", key: "submittedByFK" },
        { title: "Submitted", key: "submitTime" },
        { title: "Status", key: "statusFK" },
      ],
      inspection: null,
      findings: [],
      findingStatuses: [],
      priorities: [],
      formTemplates: [],
      loading: false,
      starting: false,
      stopping: false,
      pushingToEsri: false,
      search: "",
      showFindingModal: false,
      newFinding: {
        zoneId: "",
        inspectionId: "",
        formId: "",
        title: "",
        details: "",
        statusId: null,
        priorityId: null,
        latitude: null,
        longitude: null,
        accuracy: null,
      },
      showFullMap: false,
      showFormPicker: false, // step 1 dialog
      selectedFormId: null, // chosen template id
      selectedFormJson: null, // chosen template JSON for FormViewer
      formSubmitPayload: null,
      filledFormForSaving: null,
    };
  },
  props: {
    isEdit: Boolean,
    id: [String],
  },
  computed: {
    inspectionName() {
      return this.inspection && this.inspection.name ? this.inspection.name : 'Loading...';
    },
    filteredFindings() {
      if (!this.search) return this.findings;
      const term = this.search.toLowerCase();
      //return this.findings;
      return this.findings.filter((i) =>
        Object.values(i).some((val) => String(val).toLowerCase().includes(term))
      );
    },
    actionNeededCount() {
      const needed = this.findingStatuses.find((s) => s.findingStatus === "Action Needed");
      if (!needed) return 0;

      return this.findings.filter((f) => String(f.statusId) === String(needed.findingStatusId))
        .length;
    },
    resolvedCount() {
      const resolved = this.findingStatuses.find((s) => s.findingStatus === "Resolved");
      if (!resolved) return 0;

      return this.findings.filter((f) => String(f.statusId) === String(resolved.findingStatusId))
        .length;
    },
    inspectionTypeLabel() {
      return (
        this.inspection?.statusFK ||
        this.inspection?.template ||
        ""
      );
    },
    inspectionTypeColour() {
      const c = this.inspection?.statusColour;
      if (c) return c;
      const status = (this.inspection?.statusFK || "").toLowerCase();
      if (status.includes("plann")) return "indigo";
      if (status.includes("active") || status.includes("progress")) return "pink";
      if (status.includes("complete") || status.includes("done")) return "green";
      return "grey";
    },
    formVisualsById() {
      const out = {};
      const legacy = (code) => {
        if (!code) return "";
        const idx = iconsNums.findIndex((e) => e.icon === code);
        return idx >= 0 ? icons[idx]?.icon || "" : "";
      };
      for (const f of this.formTemplates || []) {
        const id = f.formId ?? f.FormId;
        if (id == null) continue;
        out[String(id)] = {
          iconName: f.iconName || f.IconName || legacy(f.icon ?? f.Icon) || "",
          iconColour: f.iconColour || f.IconColour || "",
          name: f.name || f.Name || "",
        };
      }
      return out;
    },
  },
  methods: {
    async onPushToEsri() {
      if (!this.inspection?.inspectionId || this.pushingToEsri) return;
      this.pushingToEsri = true;
      try {
        const { data } = await pushInspectionFindingsToEsri(this.inspection.inspectionId);
        const total = data?.total ?? 0;
        const ok = data?.succeeded ?? 0;
        const fail = data?.failed ?? 0;
        const aOk = data?.attachmentsSucceeded ?? 0;
        const aFail = data?.attachmentsFailed ?? 0;
        // Suffix appended to any non-empty success/warning toast so
        // photo-upload progress is visible alongside feature counts.
        const photoSuffix = aOk + aFail > 0
          ? (aFail === 0
              ? `, ${aOk} photo${aOk === 1 ? "" : "s"} attached`
              : `, ${aOk} photo${aOk === 1 ? "" : "s"} attached, ${aFail} photo failure${aFail === 1 ? "" : "s"}`)
          : "";

        if (total === 0) {
          this.toast?.info?.("No findings on this inspection to push.");
        } else if (fail === 0) {
          this.toast?.success?.(`Pushed ${ok} of ${total} findings to ESRI${photoSuffix}`);
        } else if (ok === 0) {
          this.toast?.error?.(`Push failed: ${data?.firstError || "see EsriPushLog"}`);
        } else {
          this.toast?.warning?.(`Pushed ${ok} of ${total} findings — ${fail} failed${photoSuffix} (${data?.firstError || "see log"})`);
        }
      } catch (e) {
        const msg = e?.response?.data?.error || e?.message || "Push failed";
        this.toast?.error?.(msg);
      } finally {
        this.pushingToEsri = false;
      }
    },
    async fetchInspection() {
      this.loading = true;
      try {
        const resp = await getById(this.id);
        if (resp) {
          this.inspection = {
            ...resp,
            lastUpdated: resp.lastUpdated || null,
          };
          if (this.inspection.inspectionId) {
            this.fetchFindings(this.inspection.inspectionId);
          }
        }
      } catch (err) {
      } finally {
        this.loading = false;
      }
    },
    onViewOnMap() {
      const inspectionId = this.inspection?.inspectionId || this.inspection?.InspectionId;
      if (!inspectionId) return;
      this.showFullMap = true;
    },
    async onPrintInspection() {
      try {

        // Better validation - check if inspection is loaded and has an ID
        const inspectionId = this.inspection?.inspectionId || this.inspection?.InspectionId;
        
        if (!inspectionId) {
          const errorMsg = !this.inspection || Array.isArray(this.inspection) 
            ? "Inspection not loaded yet. Please wait and try again." 
            : "Inspection ID missing!";
          await this.dialog.confirm({
            title: "Error",
            message: errorMsg,
            variant: "error",
            buttons: "ok"
          });
          return;
        }

        // Check if inspection is still loading
        if (this.loading) {
          const msg = "Please wait for the inspection to finish loading.";
          await this.dialog.confirm({
            title: "Please Wait",
            message: msg,
            variant: "warn",
            buttons: "ok"
          });
          return;
        }

        
        // Use the API client method
        const response = await generateInspectionReport(inspectionId);
        

        // Extract blob from axios response
        const blob = response.data;
        
        if (!blob || blob.size === 0) {
          const errorMsg = "Failed to generate inspection PDF (empty file).";
          await this.dialog.confirm({
            title: "PDF Generation Failed",
            message: errorMsg,
            variant: "error",
            buttons: "ok"
          });
          return;
        }


        // Create download link
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Inspection_${inspectionId}.pdf`;
        document.body.appendChild(a);
        a.click();
        
        // Clean up after a short delay to ensure download starts
        setTimeout(() => {
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        }, 100);

        this.toast.success("Inspection report downloaded successfully.");
      } catch (err) {
        let errorMsg = err.message || "Failed to generate inspection PDF report.";
        if (err.response?.data instanceof Blob) {
          try {
            const text = await err.response.data.text();
            const parsed = JSON.parse(text);
            errorMsg = parsed.message || text;
          } catch {
            // keep errorMsg as is
          }
        } else if (err.response?.data?.message) {
          errorMsg = err.response.data.message;
        }
        await this.dialog.confirm({
          title: "PDF Generation Failed",
          message: errorMsg,
          variant: "error",
          buttons: "ok"
        });
      }
    },

    async fetchFindings(InspectionId) {
      this.loading = true;
      try {
        const resp = await getAllByInspectionId(InspectionId);
        this.findings = resp.map((i) => ({ ...i }));
      } catch (err) {
      } finally {
        this.loading = false;
      }
    },
    async fetchFindingStatuses() {
      try {
        const result = await getLookupDataByType("FindingStatus");
        this.findingStatuses = result
          .filter(item => item.isActive)
          .map(item => ({
            FindingStatusId: item.lookupDataId,
            FindingStatus: item.name,
            findingStatusId: item.lookupDataId,
            findingStatus: item.name,
            Colour: item.colour || ""
          }));
      } catch (err) {
        this.findingStatuses = [];
      }
    },
    async fetchPriorities() {
      try {
        const result = await getLookupDataByType("FindingPriority");
        this.priorities = result
          .filter(item => item.isActive)
          .map(item => ({
            PriorityId: item.lookupDataId,
            Priority: item.name,
            priorityId: item.lookupDataId,
            priority: item.name,
            Colour: item.colour || ""
          }));
      } catch (err) {
        this.priorities = [];
      }
    },
    async fetchFormTemplates() {
      try {
        const result = await fetchFormsAll();
        this.formTemplates = result;
      } catch (err) {
        this.findingStatuses = [];
      }
    },

    getStatus(id) {
      return this.findingStatuses.find((s) => String(s.findingStatusId) === String(id));
    },

    async onAddFinding() {
      // Desktop entry point — try to grab GPS up-front; if blocked, the
      // user can still continue and the modal will show "no fix yet".
      this.resetFindingForm();
      this.formSubmitPayload = null;
      this.selectedFormId = null;
      this.selectedFormJson = null;
      try {
        const pos = await this.getCurrentPosition();
        this.applyCapturedLocation({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        });
      } catch (e) {
        // Silently fall through — modal opens with "No GPS fix yet" prompt.
      }
      this.showFormPicker = true;
    },
    resetFindingForm() {
      this.newFinding = {
        findingId: null,
        zoneId: "",
        inspectionId: "",
        title: "",
        details: "",
        statusId: null,
        priorityId: null,
        latitude: null,
        longitude: null,
        accuracy: null,
      };
    },
    applyCapturedLocation({ lat, lng, accuracy }) {
      this.newFinding.latitude = Number(lat).toFixed(6);
      this.newFinding.longitude = Number(lng).toFixed(6);
      this.newFinding.accuracy = accuracy ?? null;
    },
    onFormSubmit(payload) {
      this.formSubmitPayload = payload;
      this.toast.success("Form response attached");
    },
    onFieldMapMarkerClick({ id }) {
      // Tap a pin → open that finding for review/edit. Same destination as
      // clicking a row in the Findings table.
      if (!id) return;
      this.viewFinding(id);
    },

    getUserIconUrl(id) {
      // default to 1 if invalid, pad to two digits
      const key = String(Math.min(Math.max(parseInt(id, 10) || 1, 1), 50)).padStart(2, "0");
      return `/images/avatars/${key}.svg`;
    },
    onAddInspection() {
      this.$router.push({ name: "InspectionCreate" });
    },

    viewInspection(id) {
      this.$router.push({ name: "InspectionView", params: { id } });
    },
    onRowClick(event, payload) {
      const item = payload?.item ?? payload;
      if (item?.findingId) this.viewFinding(item.findingId);
    },
    viewFinding(id) {
      this.$router.push({ name: "FindingEdit", params: { id } });
    },
    editInspection(id) {
      this.$router.push({ name: "InspectionEdit", params: { id } });
    },
    async onDeleteInspection(id) {
      const result = await this.dialog.confirm({
        title: "Delete Inspection",
        message: "Are you sure you want to delete this inspection?",
        variant: "warn",
        buttons: "ok-cancel"
      });
      if (result !== "ok") return;
      try {
        await deleteItem(id);
        this.toast.success("Inspection deleted successfully");
        this.fetchInspections();
      } catch (err) {
      }
    },
    formatDate(dt) {
      if (!dt) return "";
      return new Date(dt).toLocaleString();
    },
    payloadToAnswers(p) {
      // Case A: native submit event from a <form>
      if (p && p.target && typeof p.isTrusted === "boolean") {
        const formEl = p.target.closest?.("form") || p.target;
        const fd = new FormData(formEl);
        const obj = {};
        for (const [k, v] of fd.entries()) {
          if (obj[k] !== undefined) {
            (Array.isArray(obj[k]) ? obj[k] : (obj[k] = [obj[k]])).push(v);
          } else {
            obj[k] = v;
          }
        }
        return obj;
      }

      // Case B: FormViewer emitted a structured payload
      if (p && typeof p === "object") {
        return p.answers || p.values || p.data || p.model || p; // common shapes
      }

      return {};
    },

    // Overlay answers onto the template array so each field’s .value contains the user’s input
    buildFilledStructure(template, answers) {
      const arr = Array.isArray(template) ? template : [];
      return arr.map((f) => {
        const out = { ...f };
        // normalize legacy key casing only once
        if ("AddPhoto" in out && !("addPhoto" in out)) {
          out.addPhoto = out.AddPhoto;
          delete out.AddPhoto;
        }
        const key = out.name || out.field || out.key || out.id;
        if (key && Object.prototype.hasOwnProperty.call(answers, key)) {
          out.value = answers[key];
        }
        return out;
      });
    },

    async chooseForm(f) {
      try {
        this.selectedFormId = f.formId;
        const raw = f.formStructure || (await fetchForm(f.formId)).formStructure;
        this.selectedFormJson = typeof raw === "string" ? JSON.parse(raw) : raw; // ensure parsed
        this.formSubmitPayload = null;
        this.filledFormForSaving = null;
        this.showFormPicker = false;
        this.showFindingModal = true;
      } catch (e) {
        this.toast.error("Could not load form");
      }
    },

    // mirror the working page: just receive what FormViewer emits, then prepare data for saving
    onFormSubmit(payload) {
      const answers = this.payloadToAnswers(payload);
      const tpl = this.selectedFormJson; // already parsed in chooseForm
      this.filledFormForSaving = this.buildFilledStructure(tpl, answers);
      this.formSubmitPayload = payload; // keep original for submitFormResponse
      this.toast.success("Form response attached");
    },

    async saveFinding() {
      try {
        if (!this.selectedFormId) return this.toast.error("Please choose a form first");
        if (!this.newFinding.latitude || !this.newFinding.longitude)
          return this.toast.error("Please select a location on the map.");
        if (!this.newFinding.priorityId) return this.toast.error("Please select a priority.");

        const selectedStatus = this.findingStatuses.find(
          (s) => Number(s.findingStatusId) === Number(this.newFinding.statusId)
        );
        if (!selectedStatus) return this.toast.error("Please select a valid status.");

        // If user didn’t click “Attach”, try to read current values from the rendered form anyway
        if (!this.filledFormForSaving) {
          const formRoot = this.$refs.formViewer?.$el?.querySelector?.("form");
          if (formRoot) {
            const fd = new FormData(formRoot);
            const answers = {};
            for (const [k, v] of fd.entries()) {
              if (answers[k] !== undefined) {
                (Array.isArray(answers[k]) ? answers[k] : (answers[k] = [answers[k]])).push(v);
              } else {
                answers[k] = v;
              }
            }
            this.filledFormForSaving = this.buildFilledStructure(this.selectedFormJson, answers);
          }
        }

        if (!this.filledFormForSaving) {
          return this.toast.error("Please fill the form and attach the response.");
        }

        // Create finding with compact JSON (no \n) in data
        if (!this.inspection || !this.inspection.inspectionId) {
          this.toast.error("Inspection not loaded. Please refresh the page.");
          return;
        }

        const payload = {
          ...this.newFinding,
          findingId: crypto.randomUUID(),
          formId: this.selectedFormId,
          zoneId: this.inspection.zoneId,
          inspectionId: this.inspection.inspectionId,
          statusId: selectedStatus.findingStatusId,
          data: JSON.stringify(this.filledFormForSaving),
        };

        const newId = await createFinding(payload);

        // Submit the same payload to responses endpoint – identical behavior to ZoneForm.vue
        if (this.formSubmitPayload) {
          await submitFormResponse(newId, this.formSubmitPayload);
        } else {
          // fallback: submit FormData we just read, in case your API expects that
          const formRoot = this.$refs.formViewer?.$el?.querySelector?.("form");
          if (formRoot) {
            await submitFormResponse(newId, new FormData(formRoot));
          }
        }

        this.toast.success("Finding created");
        this.showFindingModal = false;

        // Reload the whole inspection (details + findings) so the user lands
        // back on the same page with the new finding visible in the table
        // AND as a fresh pin on the inline map. fetchInspection re-pulls the
        // inspection metadata and triggers fetchFindings internally.
        await this.fetchInspection();

        // Scroll to top so they see the Inspection Details card first, then
        // the KPI cards / findings table / map below.
        this.$nextTick(() => {
          window.scrollTo({ top: 0, behavior: "smooth" });
        });
      } catch (err) {
        this.toast.error("Failed to create finding");
      }
    },
    async onStartInspection() {
      this.starting = true;
      try {
        // Get current GPS location
        const position = await this.getCurrentPosition();
        const latitude = position.coords.latitude;
        const longitude = position.coords.longitude;

        await startInspection(this.inspection.inspectionId, latitude, longitude);
        this.toast.success("Inspection started successfully");
        // Refresh inspection data
        await this.fetchInspection();
      } catch (err) {
        this.toast.error("Failed to start inspection. " + (err.message || ""));
      } finally {
        this.starting = false;
      }
    },
    async onStopInspection() {
      this.stopping = true;
      try {
        // Get current GPS location
        const position = await this.getCurrentPosition();
        const latitude = position.coords.latitude;
        const longitude = position.coords.longitude;

        await stopInspection(this.inspection.inspectionId, latitude, longitude);
        this.toast.success("Inspection stopped successfully");
        // Refresh inspection data
        await this.fetchInspection();
      } catch (err) {
        this.toast.error("Failed to stop inspection. " + (err.message || ""));
      } finally {
        this.stopping = false;
      }
    },
    getCurrentPosition() {
      return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error("Geolocation is not supported by your browser"));
          return;
        }
        navigator.geolocation.getCurrentPosition(
          resolve,
          reject,
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
        );
      });
    },
    formatDateTime(dateTime) {
      if (!dateTime) return "";
      const date = new Date(dateTime);
      return date.toLocaleString(undefined, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    formatDateOnly(dateTime) {
      if (!dateTime) return "";
      const date = new Date(dateTime);
      return date.toLocaleDateString(undefined, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    },
  },
  mounted() {
    this.fetchInspection();
    this.fetchFindingStatuses();
    this.fetchPriorities();
    this.fetchFormTemplates();
  },
};
</script>

<style scoped>
.inspection-header {
  margin-bottom: 8px;
}

.inspection-page-title {
  font-size: 1.9rem;
  font-weight: 700;
  line-height: 1.1;
  color: #223127;
  letter-spacing: 0.01em;
}

.inspection-page-subtitle {
  font-size: 0.98rem;
  font-weight: 500;
  color: #667085;
}

.inspection-details-card {
  border-radius: 18px;
  border: 1px solid #e8edf0;
}

.inspection-details-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.inspection-details-card__title {
  font-size: 1.25rem;
  font-weight: 800;
  color: #182230;
}

.inspection-details-card__type {
  display: flex;
  align-items: center;
  gap: 8px;
}

.inspection-details-card__type-label {
  font-size: 0.8rem;
  color: #667085;
  font-weight: 600;
}

.inspection-details-card__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px 28px;
  margin-top: 8px;
}

@media (max-width: 720px) {
  .inspection-details-card__grid {
    grid-template-columns: 1fr;
  }
}

.inspection-detail-row__label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #667085;
  margin-bottom: 2px;
}

.inspection-detail-row__value {
  font-size: 0.98rem;
  font-weight: 700;
  color: #182230;
}

.inspection-detail-row__sub {
  font-size: 0.84rem;
  font-weight: 500;
  color: #667085;
  margin-top: 2px;
  font-variant-numeric: tabular-nums;
}

.inspection-details-card__counts {
  display: flex;
  gap: 10px;
  margin-top: 18px;
  flex-wrap: wrap;
}

.count-pill {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 999px;
  border: 1px solid transparent;
}

.count-pill__num {
  font-size: 1.1rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}

.count-pill__label {
  font-size: 0.78rem;
  font-weight: 600;
}

.count-pill--total {
  background: #f3f4f6;
  border-color: #e5e7eb;
  color: #111827;
}

.count-pill--danger {
  background: #fee4e2;
  border-color: #fecdca;
  color: #b42318;
}

.count-pill--success {
  background: #d1fadf;
  border-color: #a6f4c5;
  color: #027a48;
}

.inspection-details-card__actions {
  margin-top: 18px;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
}

.inspection-muted-text {
  color: #667085;
}

.inspection-findings-card {
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid #e8edf0;
}

.inspection-findings-toolbar {
  border-bottom: 1px solid #eef2f6;
}

.submitted-by-cell {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.submitted-by-avatar {
  flex-shrink: 0;
}

.submitted-by-text {
  font-weight: 500;
  color: #344054;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.inspection-findings-table :deep(.v-data-table__tr) {
  cursor: pointer;
}
.inspection-findings-table :deep(.v-data-table__tr:hover) {
  background-color: rgba(19, 119, 82, 0.05) !important;
}
.inspection-findings-table :deep(thead th) {
  font-weight: 700 !important;
  color: #475467 !important;
  background: #fcfcfd;
}
.inspection-map-card {
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid #e8edf0;
}

.inspection-map-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: #fcfcfd;
  border-bottom: 1px solid #eef2f6;
}

.inspection-map-card__title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 700;
  color: #182230;
}

.inspection-map-card__count {
  font-size: 12px;
  font-weight: 600;
  color: #667085;
  background: #f3f4f6;
  border-radius: 999px;
  padding: 2px 10px;
  margin-left: 6px;
}

.inspection-map-card__preview {
  position: relative;
  cursor: pointer;
}

.inspection-map-card__view-full {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 16px;
  background: #ffffff;
  color: #137752;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border: 0;
  border-top: 1px solid #e8edf0;
  cursor: pointer;
  transition: background 0.15s ease;
}

.inspection-map-card__view-full:hover {
  background: #f8fbf9;
}

.fullmap-card {
  display: flex;
  flex-direction: column;
  height: 100vh;
  position: relative;
}

.fullmap-card__body {
  flex: 1 1 auto;
  min-height: 0;
}

.fullmap-card__body :deep(.field-map-wrapper) {
  height: 100% !important;
  min-height: 0 !important;
  border-radius: 0 !important;
  border: 0 !important;
}

.fullmap-fab-close {
  position: absolute !important;
  bottom: 24px;
  right: 24px;
  z-index: 5000;
}

.inspection-map-card__hint {
  font-size: 12px;
  color: #667085;
  font-weight: 500;
}

.captured-location {
  border: 1px solid #e8edf0;
  border-radius: 12px;
  padding: 10px 12px;
  background: #f8fbf9;
}

.captured-location__header {
  display: flex;
  align-items: center;
  gap: 6px;
}

.captured-location__label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #344054;
}

.captured-location__body {
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-top: 4px;
}

.captured-location__coords {
  font-size: 14px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: #182230;
}

.captured-location__accuracy {
  font-size: 12px;
  font-weight: 600;
  color: #2e7d32;
}

.captured-location__empty {
  margin-top: 4px;
  font-size: 12px;
  color: #b54708;
}
</style>
